/* Cotton Rates — many series, three sources, three units, one chart.
 *
 * The filtering all happens here rather than on the server: the whole rate
 * history is a few hundred kilobytes, and once it is in the browser every
 * unit switch and quality filter is instant. Only fetching and importing
 * go back to the API.
 */

import { useCallback, useMemo, useState } from 'react'
import { MAIN_STYLE, Subline } from '../App'
import Trade from './Trade'
import { cotton } from '../lib/api'
import type { CottonData, CottonSeries } from '../lib/api'
import { useAsync } from '../lib/useAsync'
import { useCan } from '../lib/auth'
import {
  Blueprint, ErrorBox, KpiRow, Labelled, Loading, Note, PanelHead, Segmented,
} from '../components/ui'
import { MultiLine } from '../components/charts/MultiLine'
import type { LineSeries } from '../components/charts/MultiLine'
import { change, inr, truncate } from '../lib/format'
import { seriesColor } from '../lib/scale'

type Unit = 'candy' | 'kg' | 'quintal'
type Source = 'cai' | 'agmarknet' | 'ice' | 'all'
/** The two faces of the page: the rates, and cotton's own global trade. */
type Page = 'rates' | 'trade'

const MAX_LINES = 5
const R: React.CSSProperties = { textAlign: 'right' }

const SOURCES: { value: Source; label: string }[] = [
  { value: 'cai', label: 'CAI spot' },
  { value: 'agmarknet', label: 'Mandi kapas' },
  { value: 'ice', label: 'ICE / world' },
  { value: 'all', label: 'All' },
]

const WINDOWS = [
  { value: 90, label: '90 days' },
  { value: 180, label: '6 months' },
  { value: 365, label: '1 year' },
  { value: 0, label: 'Full' },
]

/** Cotton is an input here, so a rising rate is bad news for the mill.
 *  Moves inside the dead band are left uncoloured — a rate that rounds to
 *  +0.0% is flat, and painting it red would be reading noise as a signal. */
const FLAT_PCT = 0.05

function costTone(now: number | null, then: number | null): 'good' | 'bad' | undefined {
  if (now == null || then == null || !then) return undefined
  const move = ((now - then) / then) * 100
  if (Math.abs(move) < FLAT_PCT) return undefined
  return move > 0 ? 'bad' : 'good'
}

/** Mandi kapas, as the mandi publishes it. Never converted. */
const KAPAS_UNIT = { label: '₹ / quintal of kapas', f: (v: number) => v, d: 0 }

/** How to render a rate, per unit. `f` converts from the stored ₹/candy. */
function unitDef(candyKg: number) {
  return {
    candy: { label: '₹ / candy', f: (v: number) => v, d: 0 },
    kg: { label: '₹ / kg', f: (v: number) => v / candyKg, d: 1 },
    quintal: { label: '₹ / quintal', f: (v: number) => (v / candyKg) * 100, d: 0 },
  }
}

export default function Cotton() {
  const { data, error, loading, reload, set } = useAsync(() => cotton.data(), [])

  const [page, setPage] = useState<Page>('rates')
  const [src, setSrc] = useState<Source>('cai')
  const [unit, setUnit] = useState<Unit>('candy')
  const [days, setDays] = useState(180)
  const [sel, setSel] = useState<number[] | null>(null)
  const [query, setQuery] = useState('')
  const [filters, setFilters] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState('')
  const canFetch = useCan('load')

  const runFetch = useCallback(async () => {
    setBusy(true)
    setStatus('Fetching fresh rates…')
    try {
      const res = await cotton.fetch()
      set(res.data)
      const parts = res.sources.map((s) =>
        s.skipped ? `${s.source}: already current`
          : s.ok ? `${s.source}: ${s.rows} rows`
            : `${s.source}: ${s.error}`)
      setStatus(`${res.rows} new rows over ${res.days} day${res.days === 1 ? '' : 's'} · ${parts.join(' · ')}`)
    } catch (e: any) {
      setStatus(e.message ?? String(e))
    } finally {
      setBusy(false)
    }
  }, [set])

  const tabs = <PageTabs page={page} setPage={setPage} />

  // The trade view is the Yarn page's cotton half, drawn in place. It
  // fetches its own data, so the rates need not have loaded for it.
  if (page === 'trade') {
    return (
      <main style={MAIN_STYLE}>
        {tabs}
        <Trade view="cotton" embed />
      </main>
    )
  }

  if (error) return <main style={MAIN_STYLE}>{tabs}<ErrorBox error={error} onRetry={reload} /></main>
  if (loading && !data) return <main style={MAIN_STYLE}>{tabs}<Loading what="rates" /></main>
  if (!data) return null

  return (
    <Board tabs={tabs}
      data={data} src={src} setSrc={setSrc} unit={unit} setUnit={setUnit}
      days={days} setDays={setDays}
      sel={sel} setSel={setSel} query={query} setQuery={setQuery}
      filters={filters} setFilters={setFilters}
      busy={busy} status={status} runFetch={runFetch} canFetch={canFetch}
    />
  )
}

/** The design's header nav: two plain buttons, the current one filled. */
function PageTabs({ page, setPage }: { page: Page; setPage: (p: Page) => void }) {
  const items: { value: Page; label: string }[] = [
    { value: 'rates', label: 'Cotton rates' },
    { value: 'trade', label: 'Global trade' },
  ]
  return (
    <nav className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {items.map((t) => (
        <button key={t.value} type="button"
          className={`btn ${t.value === page ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setPage(t.value)}>{t.label}</button>
      ))}
    </nav>
  )
}

interface BoardProps {
  tabs: React.ReactNode
  data: CottonData
  src: Source; setSrc: (v: Source) => void
  unit: Unit; setUnit: (v: Unit) => void
  days: number; setDays: (v: number) => void
  sel: number[] | null; setSel: (v: number[] | null) => void
  query: string; setQuery: (v: string) => void
  filters: Record<string, string>; setFilters: (v: Record<string, string>) => void
  busy: boolean; status: string; runFetch: () => void
  /** Running the collectors is for editors and admins. */
  canFetch: boolean
}

function Board(p: BoardProps) {
  const { data } = p
  const U = unitDef(data.candyKg)[p.unit]

  // --- index points by series once, sorted by date -----------------------
  const byIdx = useMemo(() => {
    const m = new Map<number, CottonData['points']>()
    data.series.forEach((s) => m.set(s.i, []))
    data.points.forEach((pt) => m.get(pt[0])?.push(pt))
    m.forEach((arr) => arr.sort((a, b) => a[1] - b[1]))
    return m
  }, [data])

  const dateMs = useMemo(
    () => data.dates.map((d) => Date.parse(d + 'T00:00:00Z')), [data.dates])

  /* Kapas is published per quintal of seed cotton and is shown that way —
     the unit switch converts lint only. Converting kapas onto a lint basis
     would need the ginning outturn *and* the cottonseed credit, and getting
     the second wrong is what previously put mandi rates ~30% high. */
  const isKapas = p.src === 'agmarknet'
  const valueOf = useCallback((s: CottonSeries, pt: CottonData['points'][0]) => {
    const raw = pt[2]
    if (raw == null) return null
    return s.basis === 'kapas' ? raw : U.f(raw)
  }, [U])

  const unitFor = useCallback(
    (s: CottonSeries) => (s.basis === 'kapas' ? KAPAS_UNIT : U), [U])

  // What the axis, KPIs and table headings say. Kapas views are fixed to
  // the mandi's own unit; lint views follow the unit switch.
  const viewUnit = isKapas ? KAPAS_UNIT : U

  const money = useCallback(
    (v: number | null, s?: CottonSeries) => {
      const u = s ? unitFor(s) : U
      return v == null ? '–' : inr(v, u.d)
    }, [U, unitFor])

  // --- filtering ---------------------------------------------------------
  const inSource = useMemo(
    () => data.series.filter((s) => (p.src === 'all' || s.src === p.src)
      && (byIdx.get(s.i)?.length ?? 0) > 0),
    [data.series, p.src, byIdx])

  const q = p.query.trim().toLowerCase()
  const inView = useMemo(() => inSource.filter((s) => {
    for (const [k, want] of Object.entries(p.filters)) {
      if (want && String((s as any)[k] ?? '') !== want) return false
    }
    if (!q) return true
    const hay = [s.label, s.market, s.variety, s.std, s.staple].filter(Boolean).join(' ').toLowerCase()
    return hay.includes(q)
  }), [inSource, p.filters, q])

  const cutoff = p.days ? Date.now() - p.days * 864e5 : 0
  const ptsOf = useCallback(
    (s: CottonSeries) => (byIdx.get(s.i) ?? []).filter((pt) => dateMs[pt[1]] >= cutoff),
    [byIdx, dateMs, cutoff])

  const withData = useMemo(
    () => inView.filter((s) => ptsOf(s).length > 1), [inView, ptsOf])

  // Default selection: the series with the most observations in the window.
  const selected = useMemo(() => {
    if (p.sel) return new Set(p.sel)
    return new Set([...withData]
      .sort((a, b) => ptsOf(b).length - ptsOf(a).length)
      .slice(0, MAX_LINES).map((s) => s.i))
  }, [p.sel, withData, ptsOf])

  const charted = useMemo(
    () => withData.filter((s) => selected.has(s.i)), [withData, selected])

  /* Under "All" the selection can span both bases. A single axis cannot
     carry ₹/quintal of kapas and ₹/candy of lint honestly — the kapas
     lines would flatten against the floor at a tenth of the scale — so the
     chart keeps to lint there and the Mandi tab shows kapas on its own. */
  const mixed = p.src === 'all' && charted.some((s) => s.basis === 'kapas')
    && charted.some((s) => s.basis !== 'kapas')
  const chartable = mixed ? charted.filter((s) => s.basis !== 'kapas') : charted

  const lines: LineSeries[] = useMemo(() => chartable.map((s) => ({
    name: truncate(s.short, 30),
    points: ptsOf(s)
      .map((pt) => ({ t: dateMs[pt[1]], v: valueOf(s, pt) }))
      .filter((x): x is { t: number; v: number } => x.v != null),
  })).filter((l) => l.points.length > 1), [chartable, ptsOf, dateMs, valueOf])

  const colorOf = new Map(charted.map((s, i) => [s.i, seriesColor(i)]))

  // --- latest / lookback helpers ----------------------------------------
  const latestOf = useCallback((s: CottonSeries) => {
    const a = byIdx.get(s.i) ?? []
    return a.length ? a[a.length - 1] : null
  }, [byIdx])

  const backBy = useCallback((s: CottonSeries, d: number) => {
    const a = byIdx.get(s.i) ?? []
    const last = a[a.length - 1]
    if (!last) return null
    const want = dateMs[last[1]] - d * 864e5
    let best: CottonData['points'][0] | null = null
    a.forEach((pt) => { if (dateMs[pt[1]] <= want) best = pt })
    return best
  }, [byIdx, dateMs])

  // --- KPIs --------------------------------------------------------------
  const lead = charted[0] ?? withData[0] ?? inView[0]
  const kpis = []
  if (lead) {
    const lp = latestOf(lead)
    const lv = lp ? valueOf(lead, lp) : null
    const wp = backBy(lead, 7); const mp = backBy(lead, 30)
    const wv = wp ? valueOf(lead, wp) : null
    const mv = mp ? valueOf(lead, mp) : null
    kpis.push({ label: `Lead series · ${viewUnit.label}`, value: money(lv, lead),
      note: `${truncate(lead.short, 34)} · ${lp ? data.dates[lp[1]] : '–'}` })
    kpis.push({ label: 'Week on week', value: change(lv, wv),
      note: `from ${money(wv)} seven days back`, tone: costTone(lv, wv) })
    kpis.push({ label: 'Month on month', value: change(lv, mv),
      note: `from ${money(mv)} thirty days back`, tone: costTone(lv, mv) })

    const latest = inView.map((s) => ({ s, v: (() => { const pt = latestOf(s); return pt ? valueOf(s, pt) : null })() }))
      .filter((x): x is { s: CottonSeries; v: number } => x.v != null)
    if (latest.length > 1) {
      const min = latest.reduce((a, b) => (a.v <= b.v ? a : b))
      const max = latest.reduce((a, b) => (a.v >= b.v ? a : b))
      kpis.push({ label: 'Spread in view', value: money(max.v - min.v),
        note: `${truncate(min.s.short, 18)} → ${truncate(max.s.short, 18)}` })
    }
  }

  // --- quality filter dropdowns, only where they discriminate ------------
  const filterDefs = (['region', 'std', 'staple', 'grade'] as const).map((key) => {
    const label = { region: 'Region', std: 'Standard', staple: 'Staple', grade: 'Grade' }[key]
    const values = [...new Set(inSource.map((s) => (s as any)[key]).filter(Boolean))]
      .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }))
    return { key, label, values }
  }).filter((f) => f.values.length > 1)

  const toggle = (i: number) => {
    const next = new Set(selected)
    if (next.has(i)) next.delete(i)
    else { if (next.size >= MAX_LINES) return; next.add(i) }
    p.setSel([...next])
  }

  const tableRows = inView
    .map((s) => ({ s, pt: latestOf(s) }))
    .filter((x): x is { s: CottonSeries; pt: CottonData['points'][0] } => x.pt !== null)
    .map((x) => ({ ...x, v: valueOf(x.s, x.pt) }))
    .sort((a, b) => (b.v ?? 0) - (a.v ?? 0))
    .slice(0, 24)

  return (
    <>
      <Subline>{data.subtitle}</Subline>

      <main style={MAIN_STYLE}>
        {p.tabs}
        <KpiRow items={kpis} min={200} />

        <section className="no-print" style={{
          display: 'flex', gap: 26, flexWrap: 'wrap', alignItems: 'flex-end',
          borderTop: '1px solid var(--color-divider)',
          borderBottom: '1px solid var(--color-divider)', padding: '16px 0',
        }}>
          <Labelled label="Source">
            <Segmented<Source> value={p.src}
              onChange={(v) => { p.setSrc(v); p.setSel(null); p.setFilters({}) }}
              options={SOURCES.map((s) => ({
                ...s,
                count: data.series.filter((x) => s.value === 'all' || x.src === s.value).length,
              }))} />
          </Labelled>

          {/* The unit switch converts lint between candy, kg and quintal.
              Kapas has one published unit and is not converted, so on that
              view the control would be a lie rather than a choice. */}
          <Labelled label="Unit">
            {isKapas ? (
              <span className="field-note" style={{ padding: '8px 0', display: 'inline-block' }}>
                ₹ / quintal — as the mandi publishes it
              </span>
            ) : (
              <Segmented<Unit> value={p.unit} onChange={p.setUnit} options={[
                { value: 'candy', label: '₹/candy' },
                { value: 'kg', label: '₹/kg' },
                { value: 'quintal', label: '₹/quintal' },
              ]} />
            )}
          </Labelled>

          <Labelled label="Window">
            <Segmented<number> value={p.days}
              onChange={(v) => { p.setDays(v); p.setSel(null) }} options={WINDOWS} />
          </Labelled>

          {filterDefs.map((f) => (
            <Labelled key={f.key} label={f.label}>
              <select value={p.filters[f.key] ?? ''} style={{ width: 180 }}
                onChange={(e) => { p.setFilters({ ...p.filters, [f.key]: e.target.value }); p.setSel(null) }}>
                <option value="">All {f.label.toLowerCase()}</option>
                {f.values.map((v) => <option key={String(v)} value={String(v)}>{String(v)}</option>)}
              </select>
            </Labelled>
          ))}

          <Labelled label="Search">
            <input type="search" placeholder="market, variety, standard" value={p.query}
              onChange={(e) => { p.setQuery(e.target.value); p.setSel(null) }}
              style={{ minWidth: 190 }} />
          </Labelled>

          <button className="btn btn-ghost"
            onClick={() => { p.setFilters({}); p.setQuery(''); p.setSel(null) }}>
            Clear filters
          </button>

          {p.canFetch ? (
            <button className="btn btn-primary" disabled={p.busy} onClick={p.runFetch}>
              {p.busy ? 'Fetching…' : 'Fetch latest'}
            </button>
          ) : null}
        </section>

        {p.status ? (
          <div className="no-print pretty" style={{ fontSize: 13, color: 'var(--color-accent-700)' }}>
            {p.status}
          </div>
        ) : null}

        <Blueprint className="sheet">
          <PanelHead title={`Rate history · ${viewUnit.label}`}
            hint={`${lines.length} of ${withData.length} series charted · ${p.days ? `last ${p.days} days` : 'full history'}`} />
          {mixed ? (
            <Note>
              Mandi kapas is left off this chart: it is seed cotton quoted per
              quintal, not lint per candy, and the two do not share an axis.
              Open <b>Mandi kapas</b> to see it as published.
            </Note>
          ) : null}
          <div style={{ marginTop: 12 }}>
            <MultiLine series={lines} labelY={(v) => inr(v, viewUnit.d)} yTitle={viewUnit.label} />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px 22px', marginTop: 14 }}>
            {lines.map((l, i) => (
              <span key={l.name + i} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontSize: 12, color: 'var(--color-neutral-800)',
              }}>
                <span style={{ width: 18, height: 2, display: 'inline-block', background: seriesColor(i) }} />
                {l.name}
                <span className="num" style={{ fontWeight: 600 }}>
                  {money(l.points[l.points.length - 1]?.v ?? null)}
                </span>
              </span>
            ))}
          </div>
        </Blueprint>

        <Blueprint className="sheet no-print">
          <PanelHead title="Series on the board"
            hint={`Up to ${MAX_LINES} at a time — click to add or drop`} />
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))',
            gap: '4px 20px', marginTop: 12, maxHeight: 250, overflow: 'auto',
          }}>
            {withData.slice(0, 60).map((s) => {
              const pt = latestOf(s)
              return (
                <button key={s.i} type="button" onClick={() => toggle(s.i)} style={{
                  display: 'flex', alignItems: 'center', gap: 9, padding: '5px 6px',
                  background: 'transparent', border: 0, borderLeft: '2px solid transparent',
                  font: 'inherit', fontSize: 13, textAlign: 'left', cursor: 'pointer',
                  color: 'var(--color-neutral-800)',
                }}>
                  <span style={{
                    width: 11, height: 11, flex: 'none',
                    border: '1px solid var(--color-neutral-500)',
                    background: selected.has(s.i) ? (colorOf.get(s.i) ?? 'var(--c-bar1)') : 'transparent',
                  }} />
                  <span className="truncate">{truncate(s.short, 32)}</span>
                  <span className="num muted" style={{ marginLeft: 'auto' }}>
                    {money(pt ? valueOf(s, pt) : null)}
                  </span>
                </button>
              )
            })}
          </div>
        </Blueprint>

        <Blueprint className="sheet">
          <PanelHead title="Latest rate by series"
            hint={`${tableRows.length} series · latest quote in ${U.label}${
              isKapas ? ' · modal price as published by the mandi' : ''}`} />
          <div className="scroll-x" style={{ marginTop: 12 }}>
            <table className="table" style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th>Series</th><th>Region</th><th>Standard</th><th>Staple</th>
                  <th style={R}>Latest</th><th style={R}>1 week</th><th style={R}>1 month</th><th>As of</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((r) => {
                  const wp = backBy(r.s, 7); const mp = backBy(r.s, 30)
                  return (
                    <tr key={r.s.i}>
                      <td style={{ fontWeight: 600 }}>{truncate(r.s.short, 30)}</td>
                      <td className="muted">{truncate(r.s.region, 26)}</td>
                      <td className="muted">{r.s.std ?? '–'}</td>
                      <td className="muted">{r.s.staple ?? '–'}</td>
                      <td className="num" style={R}>{money(r.v)}</td>
                      <td className="num" style={R}>{change(r.v, wp ? valueOf(r.s, wp) : null)}</td>
                      <td className="num" style={R}>{change(r.v, mp ? valueOf(r.s, mp) : null)}</td>
                      <td className="muted">{data.dates[r.pt[1]]}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Blueprint>

        <footer className="pretty" style={{
          fontSize: 12, color: 'var(--color-neutral-600)',
          borderTop: '1px solid var(--color-divider)', paddingTop: 14, maxWidth: 860,
        }}>
          {data.notes.map(([head, body]) => (
            <p key={head} style={{ margin: '0 0 8px' }}>
              <b style={{ color: 'var(--color-neutral-700)' }}>{head}</b> {body}
            </p>
          ))}
          <p style={{ margin: 0 }}>Built {data.generated}.</p>
        </footer>
      </main>
    </>
  )
}
